
import { supplements } from '../../../mocks/prestations';

interface SummaryProps {
  data: any;
  /** Returns the total price of selected options */
  getOptionsTotal: () => number;
  /** Calculates the overall total (base price + options + travel, etc.) */
  calculateTotal: () => number;
}

const GARAGE_ADDRESS = 'Z.I. Champ cheval 2, 1530 Payerne, Suisse';

export default function Summary({ data, getOptionsTotal, calculateTotal }: SummaryProps) {
  // Defensive checks – ensure required fields exist to avoid runtime crashes
  const prestationName = data?.prestation?.name ?? 'Prestation inconnue';
  const prestationSize = data?.size ?? '';
  const basePrice = typeof data?.price === 'number' ? data.price : 0;
  const travelCost = typeof data?.travelCost === 'number' ? data.travelCost : 0;
  const distance = typeof data?.distance === 'number' ? data.distance : 0;
  const location = data?.location ?? 'garage';
  const address = data?.address ?? '';
  const postalCode = data?.postalCode ?? '';
  const city = data?.city ?? '';
  const date = data?.date ? new Date(data.date) : null;
  const time = data?.time ?? '';

  return (
    <div className="bg-dark-bg border border-dark-border rounded-xl p-6 h-fit sticky top-4">
      <h3 className="text-xl font-bold text-white mb-6 flex items-center">
        <i className="ri-file-list-3-line text-primary mr-2"></i>
        Récapitulatif
      </h3>

      <div className="space-y-4">
        {/* Base prestation */}
        <div className="pb-4 border-b border-dark-border">
          <div className="flex justify-between items-start mb-2">
            <div>
              <p className="text-white font-semibold">{prestationName}</p>
              <p className="text-gray-400 text-sm">{prestationSize}</p>
            </div>
            <p className="text-primary font-bold">{basePrice} CHF</p>
          </div>
        </div>

        {/* Options */}
        {Array.isArray(data?.options) && data.options.length > 0 && (
          <div className="pb-4 border-b border-dark-border">
            <p className="text-gray-400 text-sm mb-2">Options supplémentaires</p>
            {data.options.map((optionId: string) => {
              const option = supplements.find((s) => s.id === optionId);
              return option ? (
                <div key={optionId} className="flex justify-between items-center mb-1">
                  <p className="text-white text-sm">{option.name}</p>
                  <p className="text-primary text-sm">+{option.price} CHF</p>
                </div>
              ) : null;
            })}
          </div>
        )}

        {/* Travel cost */}
        {location === 'domicile' && distance > 0 && (
          <div className="pb-4 border-b border-dark-border">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-white font-semibold">Frais de déplacement</p>
                <p className="text-gray-400 text-sm">{distance} km × 1.50 CHF/km</p>
              </div>
              <p className="text-primary font-bold">{travelCost} CHF</p>
            </div>
          </div>
        )}

        {/* Date & time */}
        <div className="pb-4 border-b border-dark-border">
          <p className="text-gray-400 text-sm mb-1">Date et heure</p>
          <p className="text-white font-semibold">
            {date
              ? date.toLocaleDateString('fr-FR', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })
              : 'Date non renseignée'}
          </p>
          <p className="text-white">à {time}</p>
        </div>

        {/* Service location */}
        <div className="pb-4 border-b border-dark-border">
          <p className="text-gray-400 text-sm mb-1">Lieu du service</p>
          {location === 'garage' ? (
            <div>
              <p className="text-white font-semibold">Au Garage</p>
              <p className="text-gray-400 text-sm">{GARAGE_ADDRESS}</p>
            </div>
          ) : (
            <div>
              <p className="text-white font-semibold">Prise en charge</p>
              <p className="text-gray-400 text-sm">
                {address}, {postalCode} {city}
              </p>
              {distance > 0 && (
                <p className="text-gray-400 text-xs mt-1">Distance : {distance} km</p>
              )}
            </div>
          )}
        </div>

        {/* Total */}
        <div className="pt-2">
          <div className="flex justify-between items-center">
            <p className="text-xl font-bold text-white">Total</p>
            <p className="text-2xl font-bold text-primary">{calculateTotal()} CHF</p>
          </div>
          <p className="text-xs text-gray-500 mt-2">À régler après la prestation</p>
        </div>
      </div>
    </div>
  );
}
